import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Agent } from '@atproto/api'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/hooks/useToast'
import {
    fetchUserPreferences,
    saveUserPreference,
    userPrefsQueryKey,
    type UserPreferences,
    type TabEntry,
    type TabsPreference,
} from '@/services/userPreferencesService'
import { saveFeedPreferences } from '@/services/feedsService'
import { strings } from '@/strings'
import type { ResolvedFeed, SavedFeed } from '@/types/feeds'

/** Sentinel ID for the virtual Following tab (not backed by a Bluesky feed record) */
export const FOLLOWING_TAB_ID = 'following'
/**
 * Legacy sentinel ID for the Articles tab, which moved to its own bottom-nav
 * route (issue #2393) and is no longer part of the unified tab list. Kept
 * only so `reconcileTabs` can strip any stale stored entry with this id.
 */
export const ARTICLES_TAB_ID = 'articles'

/** A unified tab descriptor used by the Home page and Feeds settings page */
export interface UnifiedTab {
    /** 'following' | AT URI for custom feeds */
    id: string
    label: string
    visible: boolean
    /** Position in the unified order (0-based) */
    position: number
    /** Whether this tab can be dragged */
    isLocked?: boolean
    /** ResolvedFeed metadata for custom feeds */
    feed?: ResolvedFeed
}

export interface UseMackinacTabPrefsResult {
    /** Whether article card clicks open the publisher URL instead of the in-app reader. Defaults to false. */
    openArticlesExternally: boolean
    /** Persist the openArticlesExternally preference. */
    setOpenArticlesExternally: (value: boolean) => Promise<void>
    /**
     * Full ordered list of unified tabs, reconciled with Bluesky savedFeedsPrefV2.
     * Only includes tabs that are relevant for the current protocol / feature flags.
     */
    unifiedTabs: UnifiedTab[]
    /** Save the full unified tabs order+visibility to mackinac and fan out to Bluesky. */
    saveUnifiedTabs: (tabs: UnifiedTab[]) => Promise<void>
    isLoading: boolean
    isSaving: boolean
}

/**
 * Reconcile mackinac tab prefs with Bluesky savedFeedsPrefV2 items.
 *
 * Rules:
 * 1. If no mackinac tabs exist → use Bluesky order as-is, all visible.
 * 2. Start with mackinac tabs in their stored order.
 * 3. Add any Bluesky feed items not present in mackinac tabs (appended at end, visible=true).
 * 4. Remove any mackinac tabs whose id is a custom feed URI not present in the Bluesky list.
 *    (Following is never removed by Bluesky-side changes.) Any stored entry with
 *    id === ARTICLES_TAB_ID is dropped — Articles moved to its own bottom-nav route
 *    (issue #2393) and is no longer part of the unified tab list.
 * 5. Re-normalize positions to 0-based sequential.
 *
 * @param mackinacTabs  Stored mackinac TabEntry[] (may be empty)
 * @param bskyItems     Items from savedFeedsPrefV2 (only pinned, type=feed/list/timeline)
 * @returns Reconciled TabEntry[] with stable positions
 */
export function reconcileTabs(mackinacTabs: TabEntry[], bskyItems: SavedFeed[]): TabEntry[] {
    // First-time user: no mackinac tabs stored yet — derive from Bluesky order
    if (mackinacTabs.length === 0) {
        const result: TabEntry[] = []
        let pos = 0

        result.push({ id: FOLLOWING_TAB_ID, visible: true, position: pos++ })

        // Custom feeds in Bluesky pinned order
        for (const item of bskyItems) {
            if (!item.pinned) continue
            if (item.type === 'timeline' && item.value === 'following') continue
            result.push({ id: item.value, visible: true, position: pos++ })
        }

        return result
    }

    // Existing mackinac tabs: reconcile against current Bluesky state

    // Build a set of all Bluesky feed ids (pinned or unpinned) for existence checks
    const allBskyIds = new Set(
        bskyItems.map(f =>
            f.type === 'timeline' && f.value === 'following' ? FOLLOWING_TAB_ID : f.value
        )
    )

    // Step 1: Drop any stale Articles entry (attrition), keep Following unconditionally, and
    // remove mackinac custom-feed tabs whose URI is no longer in Bluesky at all.
    // A tab with visible=false is intentionally unpinned (moved to Saved Feeds) — keep it
    // as long as it still exists in Bluesky's saved feed list (pinned or unpinned).
    const kept = mackinacTabs.filter(t => {
        if (t.id === ARTICLES_TAB_ID) return false
        if (t.id === FOLLOWING_TAB_ID) return true
        // Custom feed: keep if present in Bluesky (regardless of pinned state)
        return allBskyIds.has(t.id)
    })

    // Step 2: Add Bluesky feeds missing from mackinac tabs (newly pinned feeds — not already tracked)
    const mackinacIds = new Set(kept.map(t => t.id))
    const toAdd: TabEntry[] = []
    let insertPos = kept.length
    for (const item of bskyItems) {
        if (!item.pinned) continue
        const feedId =
            item.type === 'timeline' && item.value === 'following' ? FOLLOWING_TAB_ID : item.value
        if (!mackinacIds.has(feedId)) {
            toAdd.push({ id: feedId, visible: true, position: insertPos++ })
        }
    }

    // Step 3: Ensure Following is always present (append at end if somehow missing)
    let merged = [...kept, ...toAdd]
    const hasFollowing = merged.some(t => t.id === FOLLOWING_TAB_ID)
    if (!hasFollowing) {
        merged = [...merged, { id: FOLLOWING_TAB_ID, visible: true, position: merged.length }]
    }

    // Re-normalize positions to 0-based sequential
    return merged.map((t, i) => ({ ...t, position: i }))
}

/**
 * Build UnifiedTab[] from a reconciled TabEntry[] and the user's saved feeds metadata.
 *
 * Defensively drops any stale ARTICLES_TAB_ID entry itself — this is exported and can be
 * called with unreconciled entries, so it cannot rely on reconcileTabs having stripped one.
 */
export function buildUnifiedTabs(entries: TabEntry[], resolvedFeeds: ResolvedFeed[]): UnifiedTab[] {
    const feedMap = new Map(resolvedFeeds.map(f => [f.uri, f]))

    return entries
        .filter(entry => entry.id !== ARTICLES_TAB_ID)
        .slice()
        .sort((a, b) => a.position - b.position)
        .map(entry => {
            if (entry.id === FOLLOWING_TAB_ID) {
                return {
                    id: FOLLOWING_TAB_ID,
                    label: strings.feeds.followingRowLabel,
                    visible: entry.visible,
                    position: entry.position,
                }
            }
            const feed = feedMap.get(entry.id)
            return {
                id: entry.id,
                label: feed?.displayName ?? entry.id,
                visible: entry.visible,
                position: entry.position,
                feed,
            }
        })
}

/**
 * Hook to read and write mackinac-specific tab preferences from the
 * xrpc-server user preferences endpoint. Only available when the user is
 * authenticated with Bluesky (AT Protocol).
 *
 * Manages a full unified tab order (Following, custom pinned feeds) that is
 * reconciled with Bluesky savedFeedsPrefV2 on load. Writes fan out to
 * mackinac first (source of truth), then to Bluesky.
 */
export function useMackinacTabPrefs(options?: {
    /** Bluesky savedFeedsPrefV2 items for reconciliation */
    bskyItems?: SavedFeed[]
    /** Resolved feeds for label/metadata lookup */
    resolvedFeeds?: ResolvedFeed[]
}): UseMackinacTabPrefsResult {
    const { session } = useAuth()
    const queryClient = useQueryClient()
    const queryKey = userPrefsQueryKey(session?.did)
    const { showToast } = useToast()

    const bskyItems = useMemo(() => options?.bskyItems ?? [], [options?.bskyItems])
    const resolvedFeeds = useMemo(() => options?.resolvedFeeds ?? [], [options?.resolvedFeeds])

    const { data: prefs, isLoading } = useQuery<UserPreferences>({
        queryKey,
        queryFn: async () => {
            if (!session) return {}
            const agent = new Agent(session)
            return fetchUserPreferences(agent)
        },
        enabled: !!session,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    })

    const openArticlesExternally = prefs?.openArticlesExternally ?? false

    // Reconcile mackinac tabs with Bluesky state to produce unified ordered list.
    // useMemo stabilizes the array reference so callers' useEffect deps don't fire on every render.
    const unifiedTabs = useMemo(() => {
        const reconciledEntries = reconcileTabs(prefs?.tabs?.tabs ?? [], bskyItems)
        return buildUnifiedTabs(reconciledEntries, resolvedFeeds)
    }, [prefs, bskyItems, resolvedFeeds])

    // Mutation for saving the full unified tabs structure
    const tabsMutation = useMutation({
        mutationFn: async (tabs: UnifiedTab[]) => {
            if (!session) throw new Error('Not authenticated')
            const agent = new Agent(session)

            const tabEntries: TabEntry[] = tabs.map((t, i) => ({
                id: t.id,
                visible: t.visible,
                position: i,
            }))
            const tabsPref: TabsPreference = { version: 1, tabs: tabEntries }

            // 1. Save to mackinac xrpc (source of truth)
            await saveUserPreference(agent, 'tabs', tabsPref)

            // 2. Fan out to Bluesky savedFeedsPrefV2 (best-effort — mackinac is source of truth)
            try {
                // Build a lookup from tab ID to SavedFeed metadata
                const bskyItemMap = new Map<string, SavedFeed>()
                for (const item of bskyItems) {
                    const key =
                        item.type === 'timeline' && item.value === 'following'
                            ? FOLLOWING_TAB_ID
                            : item.value
                    bskyItemMap.set(key, item)
                }

                // Reorder pinned items to match the mackinac order.
                // A tab with visible=false is being moved to the Saved Feeds section — mark it
                // pinned:false in Bluesky so it appears as an unpinned saved feed there too.
                const reorderedPinned: SavedFeed[] = []
                const unpinnedFromMackinac: SavedFeed[] = []
                for (const tab of tabs) {
                    const bskyEntry = bskyItemMap.get(tab.id)
                    if (bskyEntry) {
                        if (tab.visible) {
                            reorderedPinned.push({ ...bskyEntry, pinned: true })
                        } else {
                            unpinnedFromMackinac.push({ ...bskyEntry, pinned: false })
                        }
                    }
                }

                // Append any unpinned Bluesky feeds not represented in the unified tabs
                const handledIds = new Set([
                    ...reorderedPinned.map(f => f.id),
                    ...unpinnedFromMackinac.map(f => f.id),
                ])
                const unpinnedRest = bskyItems.filter(f => !handledIds.has(f.id) && !f.pinned)

                await saveFeedPreferences(session, [
                    ...reorderedPinned,
                    ...unpinnedFromMackinac,
                    ...unpinnedRest,
                ])
            } catch {
                // Bluesky sync failed — show informational toast, do not throw
                // Mackinac order is persisted; Bluesky will reconcile on next load
                showToast(strings.feeds.blueskyFanoutError, 'info')
            }
        },
        onMutate: async (tabs: UnifiedTab[]) => {
            await queryClient.cancelQueries({ queryKey })
            const previous = queryClient.getQueryData<UserPreferences>(queryKey)

            const tabEntries: TabEntry[] = tabs.map((t, i) => ({
                id: t.id,
                visible: t.visible,
                position: i,
            }))
            queryClient.setQueryData<UserPreferences>(queryKey, old => ({
                ...old,
                tabs: { version: 1, tabs: tabEntries },
            }))
            return { previous }
        },
        onError: (_err, _vars, context) => {
            if (context?.previous !== undefined) {
                queryClient.setQueryData<UserPreferences>(queryKey, context.previous)
            }
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey })
        },
    })

    const openArticlesExternallyMutation = useMutation({
        mutationFn: async (value: boolean) => {
            if (!session) throw new Error('Not authenticated')
            const agent = new Agent(session)
            await saveUserPreference(agent, 'openArticlesExternally', value)
        },
        onMutate: async (value: boolean) => {
            await queryClient.cancelQueries({ queryKey })
            const previous = queryClient.getQueryData<UserPreferences>(queryKey)
            queryClient.setQueryData<UserPreferences>(queryKey, old => ({
                ...old,
                openArticlesExternally: value,
            }))
            return { previous }
        },
        onError: (_err, _vars, context) => {
            if (context?.previous !== undefined) {
                queryClient.setQueryData<UserPreferences>(queryKey, context.previous)
            }
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey })
        },
    })

    return {
        openArticlesExternally,
        setOpenArticlesExternally: openArticlesExternallyMutation.mutateAsync,
        unifiedTabs,
        saveUnifiedTabs: (tabs: UnifiedTab[]) => tabsMutation.mutateAsync(tabs),
        isLoading,
        isSaving: tabsMutation.isPending || openArticlesExternallyMutation.isPending,
    }
}
